import { strapiData } from "@/lib/strapi/strapiApi";
import {
  convertStripeDecimalPriceToPrice,
  prettyCurrency,
} from "@/lib/stripe/formatters";
import { Subject, SubTopic, Topic } from "@/types/subject-schema";
import { Test } from "@/types/test-schema";
import { metaCacheTags } from "@utils/cacheTags";

/**
 * Formats an amount (in minor units, e.g. cents) with the given currency.
 * Used across profile subscription components for consistent price display.
 *
 * @param amountInMinorUnits - amount in cents/smallest unit (e.g. Stripe amount)
 * @param currency - currency code (gbp, usd, eur)
 * @param options - optional suffix (e.g. '/mo') or abs (use Math.abs on amount)
 */
export function formatPrice(
  amountInMinorUnits: number,
  currency: string,
  options?: { suffix?: string; abs?: boolean }
): string {
  const amount = options?.abs
    ? Math.abs(amountInMinorUnits)
    : amountInMinorUnits;
  const base = `${prettyCurrency(currency)}${convertStripeDecimalPriceToPrice(
    amount,
    true
  ).toFixed(2)}`;
  return options?.suffix ? `${base}${options.suffix}` : base;
}

export async function getProfilePageSubjects(): Promise<
  {
    documentId: Subject["documentId"];
    Name: Subject["Name"];
    RelatedTopics: {
      documentId: Topic["documentId"];
      RelatedSubTopics: Pick<SubTopic, "documentId">[];
      RelatedTopicTests: {
        documentId: Test["documentId"];
        QuestionSource: Test["QuestionSource"];
        IsHigherLevel: Test["IsHigherLevel"];
      }[];
    }[];
  }[]
> {
  const qs = require("qs");
  const params = qs.stringify(
    {
      status: "published",
      fields: ["Name"],
      populate: {
        RelatedTopics: {
          fields: ["TopicNumber"],
          sort: ["TopicNumber"],
          populate: {
            RelatedSubTopics: {
              fields: ["documentId"],
              sort: ["OfficialTag", "IsHigherLevel", "SubTopicNumber"],
            },
            RelatedTopicTests: {
              fields: ["QuestionSource", "IsHigherLevel"],
            },
          },
        },
      },
      sort: ["Name"],
    },
    {
      encodeValuesOnly: true,
    }
  );

  return await strapiData(
    process.env.STRAPI_API_TOKEN as string,
    "subjects",
    params,
    undefined,
    undefined,
    undefined,
    undefined,
    metaCacheTags(),
  );
}
